import React, { useState } from "react";

import { RunStatus, StoredRun } from "./runStore";

const STATUS_META: Record<
  RunStatus,
  { label: string; dot: string; text: string }
> = {
  running: { label: "Running", dot: "bg-indigo-500", text: "text-indigo-700" },
  completed: { label: "Ready", dot: "bg-green-500", text: "text-green-700" },
  failed: { label: "Failed", dot: "bg-red-500", text: "text-red-700" },
  cancelled: { label: "Cancelled", dot: "bg-gray-400", text: "text-gray-500" },
};

const timeAgo = (ts: number): string => {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

interface RunHistoryPanelProps<TPayload> {
  runs: StoredRun<TPayload>[];
  onReload: (run: StoredRun<TPayload>) => void;
  onDelete: (id: string) => void;
  onClearAll?: () => void;
  /** Panel heading. */
  title?: string;
  /** One-line explainer under the heading. */
  subtitle?: string;
  /** Custom per-run status/counts line (defaults to status + counts rollup). */
  renderMeta?: (run: StoredRun<TPayload>) => React.ReactNode;
  /** Whether a run can be reloaded (defaults to: has a payload). */
  canReload?: (run: StoredRun<TPayload>) => boolean;
  className?: string;
}

const defaultCounts = (counts?: Record<string, number>): string => {
  if (!counts) return "";
  const parts = Object.entries(counts)
    .filter(([, v]) => typeof v === "number")
    .map(([k, v]) => `${v} ${k}`);
  return parts.join(" · ");
};

/**
 * A compact, collapsible "recent runs" list for a workflow. Reload restores a
 * previous run's data (so an accidental tab close / view change doesn't lose
 * everything); Delete drops it from storage. Status + counts (e.g. how many
 * items were written/added) come from the persisted run so progress survives
 * reloads. Generic over the run payload type.
 */
export function RunHistoryPanel<TPayload>({
  runs,
  onReload,
  onDelete,
  onClearAll,
  title = "Recent runs",
  subtitle,
  renderMeta,
  canReload,
  className = "",
}: RunHistoryPanelProps<TPayload>): React.ReactElement | null {
  const [collapsed, setCollapsed] = useState(false);
  if (!runs || runs.length === 0) return null;

  return (
    <div className={`rounded-lg border border-gray-200 bg-white ${className}`}>
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="flex min-w-0 items-center gap-2 text-left"
        >
          <span className="text-sm">🕘</span>
          <span className="text-sm font-semibold text-gray-800">{title}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
            {runs.length}
          </span>
          <span className="text-xs text-gray-400">{collapsed ? "▸" : "▾"}</span>
        </button>
        {onClearAll && !collapsed && (
          <button
            type="button"
            onClick={onClearAll}
            className="flex-shrink-0 text-xs font-medium text-gray-400 underline decoration-dotted hover:text-gray-700"
          >
            Clear all
          </button>
        )}
      </div>

      {!collapsed && (
        <>
          {subtitle && (
            <p className="px-4 pt-2 text-xs text-gray-500">{subtitle}</p>
          )}
          <ul className="max-h-72 divide-y divide-gray-100 overflow-auto">
            {runs.map((run) => {
              const meta = STATUS_META[run.status];
              const reloadable = canReload
                ? canReload(run)
                : run.payload !== undefined;
              const countsLine = defaultCounts(run.counts);
              return (
                <li key={run.id} className="px-4 py-2.5">
                  <div className="flex items-start gap-2">
                    <span
                      className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${meta.dot}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-gray-900">
                          {run.label}
                        </span>
                        <span className="flex-shrink-0 text-[11px] text-gray-400">
                          {timeAgo(run.createdAt)}
                        </span>
                      </div>
                      <div className={`text-xs ${meta.text}`}>
                        {renderMeta ? (
                          renderMeta(run)
                        ) : (
                          <>
                            {meta.label}
                            {countsLine ? ` · ${countsLine}` : ""}
                          </>
                        )}
                      </div>
                      {run.status === "failed" && run.error && (
                        <div className="mt-0.5 truncate text-xs text-red-500">
                          {run.error}
                        </div>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          disabled={!reloadable}
                          onClick={() => onReload(run)}
                          className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                          title={
                            reloadable
                              ? "Reload this run's data"
                              : "This run's data is no longer cached"
                          }
                        >
                          Reload
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(run.id)}
                          className="text-xs font-medium text-gray-500 underline decoration-dotted hover:text-gray-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

export default RunHistoryPanel;
